import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { PageHeader } from "@/components/common/PageHeader";
import { profileApi } from "@/lib/api";
import { useAuthStore } from "@/lib/auth/store";
import { initials } from "@/lib/utils";

const fieldLabel = "mb-1 block text-xs font-semibold text-muted-foreground";

export default function ProfilePage() {
  const qc = useQueryClient();
  const setSession = useAuthStore((s) => s.setSession);
  const session = useAuthStore((s) => s.session);

  const me = useQuery({
    queryKey: ["profile", "me"],
    queryFn: () => profileApi.me().then((r) => r.data.data),
  });

  const [name, setName] = useState("");
  const [photoUrl, setPhotoUrl] = useState("");
  const [address, setAddress] = useState("");
  const [gender, setGender] = useState("");
  const [bio, setBio] = useState("");
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const u = me.data;
    if (u) {
      setName(u.name ?? "");
      setPhotoUrl(u.photoUrl ?? "");
      setAddress(u.address ?? "");
      setGender(u.gender ?? "");
      setBio(u.bio ?? "");
    }
  }, [me.data]);

  const save = useMutation({
    mutationFn: () =>
      profileApi.update({
        name: name.trim(),
        photoUrl: photoUrl.trim(),
        address: address.trim(),
        gender: gender.trim(),
        bio: bio.trim(),
      }),
    onSuccess: (res) => {
      const updated = res.data.data;
      qc.invalidateQueries({ queryKey: ["profile"] });
      if (updated && session) {
        setSession({ ...session, name: updated.name, photoUrl: updated.photoUrl });
      }
      setSaved(true);
      window.setTimeout(() => setSaved(false), 2500);
    },
  });

  return (
    <>
      <PageHeader title="My profile" subtitle="Manage your admin account details." />

      <div className="grid gap-4 lg:grid-cols-3">
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle>Account</CardTitle>
            <CardDescription>Read-only identity details.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Avatar className="size-14">
                {photoUrl && <AvatarImage src={photoUrl} alt={name} />}
                <AvatarFallback>{initials(name || me.data?.email || "A")}</AvatarFallback>
              </Avatar>
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">{me.data?.name ?? "—"}</p>
                <p className="truncate text-xs text-muted-foreground">{me.data?.email ?? "—"}</p>
              </div>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Role</span>
              <Badge variant="sunset">{me.data?.role ?? "ADMIN"}</Badge>
            </div>
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Phone</span>
              <span>{me.data?.phoneNumber ?? "—"}</span>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle>Edit profile</CardTitle>
            <CardDescription>Update your display details.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className={fieldLabel}>Full name</label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Your name" />
            </div>
            <div>
              <label className={fieldLabel}>Photo URL</label>
              <Input value={photoUrl} onChange={(e) => setPhotoUrl(e.target.value)} placeholder="https://…" />
            </div>
            <div>
              <label className={fieldLabel}>Address</label>
              <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="City, Country" />
            </div>
            <div>
              <label className={fieldLabel}>Gender</label>
              <select
                className="w-full cursor-pointer rounded-md border border-border bg-card px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                value={gender}
                onChange={(e) => setGender(e.target.value)}
              >
                <option value="">—</option>
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
                <option value="OTHER">Other</option>
              </select>
            </div>
            <div>
              <label className={fieldLabel}>Bio</label>
              <textarea
                value={bio}
                onChange={(e) => setBio(e.target.value)}
                rows={3}
                placeholder="A short bio"
                className="w-full resize-none rounded-md border border-border bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </div>

            {save.isError && <p className="text-sm text-destructive">Couldn't save changes. Try again.</p>}

            <div className="flex items-center gap-3 pt-2">
              <Button disabled={save.isPending || me.isLoading || name.trim().length === 0} onClick={() => save.mutate()}>
                {save.isPending ? "Saving…" : "Save changes"}
              </Button>
              {saved && (
                <span className="inline-flex items-center gap-1 text-sm text-success">
                  <CheckCircle2 className="size-4" /> Saved
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
